interface RetryOptions {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
}

export const withRetry = async <T>(
  fn: () => Promise<T>, 
  options: RetryOptions = {}
): Promise<T> => {
  const config = {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 4000,
    backoffMultiplier: 2,
    ...options
  };

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      console.log(`[retryUtils] Attempt ${attempt}/${config.maxAttempts}`);
      const result = await fn();
      console.log(`[retryUtils] Success on attempt ${attempt}`);
      return result;
    } catch (error) {
      console.error(`[retryUtils] Attempt ${attempt} failed:`, error);
      
      if (attempt === config.maxAttempts) {
        throw error;
      }
      
      const delay = Math.min(
        config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1), 
        config.maxDelay
      );
      console.log(`[retryUtils] Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Retry loop completed without result');
};

export const withTimeout = <T>(
  promise: Promise<T>, 
  timeoutMs: number, 
  errorMessage = "Operation timed out"
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(errorMessage));
      }, timeoutMs);
    })
  ]);
};
